import React, { useState, useEffect } from 'react';
import {
    Box,
    Container,
    CssBaseline,
    ThemeProvider,
    Typography,
    Grid,
    CardContent,
    IconButton,
    Button,
    Dialog,
    Divider,
    CircularProgress,
    Alert,
    Paper,
    Skeleton,
    DialogTitle,
    DialogContent
} from "@mui/material";
import { Edit, Add } from "@mui/icons-material";
import { getLegals, createLegalDocument, updateLegalDocument } from "../../../context/Legals";
import PostForm from "../post/PostForm";
import darkTheme from "../dashboardTheme";

const DocumentComponent = () => {
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [currentDocument, setCurrentDocument] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const documentTypes = [
        'termsAndConditions',
        'privacyPolicy',
        'cookiePolicy',
        'aboutUs',
        'languagePolicy',
        'communityGuidelines'
    ];

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        try {
            setLoading(true);
            const response = await getLegals();
            setDocuments(response.legalDocuments || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (document) => {
        setCurrentDocument(document);
        setIsEditing(true);
        setEditDialogOpen(true);
    };

    const handleAddClick = (label) => {
        setCurrentDocument({
            label,
            title: getDefaultTitle(label),
            content: '<p>Start writing your content here...</p>',
            status: "active"
        });
        setIsEditing(false);
        setEditDialogOpen(true);
    };

    const getDefaultTitle = (label) => {
        switch(label) {
            case 'termsAndConditions': return 'Terms and Conditions';
            case 'privacyPolicy': return 'Privacy Policy';
            case 'cookiePolicy': return 'Cookie Policy';
            case 'aboutUs': return 'About Us';
            case 'languagePolicy': return 'Language Policy';
            case 'communityGuidelines': return 'Community Guidelines';
            default: return 'New Document';
        }
    };

    const handleSave = async (documentData) => {
        try {
            if (isEditing) {
                await updateLegalDocument(documentData.label, documentData);
            } else {
                await createLegalDocument(documentData);
            }
            fetchDocuments();
            setEditDialogOpen(false);
        } catch (err) {
            setError(err.message);
        }
    };

    const getLabelDisplayName = (label) => {
        switch(label) {
            case 'termsAndConditions': return 'Terms & Conditions';
            case 'privacyPolicy': return 'Privacy Policy';
            case 'cookiePolicy': return 'Cookie Policy';
            case 'aboutUs': return 'About Us';
            case 'languagePolicy': return 'Language Policy';
            case 'communityGuidelines': return 'Community Guidelines';
            default: return label;
        }
    };

    const getDocumentContentPreview = (content) => {
        if (!content) return '';
        const plainText = content.replace(/<[^>]*>/g, '');
        return plainText.length > 150 ? `${plainText.substring(0, 150)}...` : plainText;
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom sx={{
                    fontWeight: 600,
                    mb: 4,
                    color: 'primary.main',
                    textAlign: 'center'
                }}>
                    Content Management
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        {error}
                    </Alert>
                )}

                {loading ? (
                    <Grid container spacing={3}>
                        {documentTypes.map((label) => (
                            <Grid item xs={12} md={6} lg={3} key={label}>
                                <Skeleton variant="rectangular" height={200} />
                            </Grid>
                        ))}
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        {documentTypes.map((label) => {
                            const document = documents.find(doc => doc.label === label);
                            return (
                                <Grid item xs={12} md={6} lg={3} key={label}>
                                    <Paper elevation={3} sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        border: document ? '1px solid #c7a45d' : '1px solid #444',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-5px)',
                                            boxShadow: '0 10px 20px rgba(0,0,0,0.3)'
                                        }
                                    }}>
                                        <CardContent sx={{ flexGrow: 1 }}>
                                            <Typography variant="h6" component="h2" sx={{
                                                mb: 2,
                                                color: 'primary.main',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}>
                                                {getLabelDisplayName(label)}
                                                {document && (
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleEditClick(document)}
                                                        size="small"
                                                    >
                                                        <Edit fontSize="small" />
                                                    </IconButton>
                                                )}
                                            </Typography>

                                            {document ? (
                                                <>
                                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                                        {document.title}
                                                    </Typography>
                                                    <Typography variant="body2" color="textSecondary" sx={{
                                                        mb: 2,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical'
                                                    }}>
                                                        {getDocumentContentPreview(document.content)}
                                                    </Typography>
                                                    <Typography variant="caption" color="textSecondary">
                                                        Last updated: {new Date(document.updatedAt).toLocaleDateString()}
                                                    </Typography>
                                                </>
                                            ) : (
                                                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                                    No {getLabelDisplayName(label).toLowerCase()} content created yet.
                                                </Typography>
                                            )}
                                        </CardContent>

                                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                            {document ? (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    onClick={() => handleEditClick(document)}
                                                    startIcon={<Edit />}
                                                    sx={{ mr: 1 }}
                                                >
                                                    Edit
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    onClick={() => handleAddClick(label)}
                                                    startIcon={<Add />}
                                                >
                                                    Create
                                                </Button>
                                            )}
                                        </Box>
                                    </Paper>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}

                <Dialog
                    open={editDialogOpen}
                    onClose={() => setEditDialogOpen(false)}
                    fullWidth
                    maxWidth="md"
                    PaperProps={{
                        sx: {
                            background: darkTheme.palette.background.paper,
                            minHeight: '80vh'
                        }
                    }}
                >
                    <DialogTitle sx={{
                        borderBottom: '1px solid #444',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        {isEditing ? 'Edit' : 'Create'} {currentDocument && getLabelDisplayName(currentDocument.label)}
                    </DialogTitle>
                    <DialogContent dividers sx={{ p: 0 }}>
                        {currentDocument && (
                            <PostForm
                                post={currentDocument}
                                onSave={handleSave}
                                onEdit={handleSave}
                                onCancel={() => setEditDialogOpen(false)}
                                showActive={false}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            </Container>
        </ThemeProvider>
    );
};

export default DocumentComponent;